/**
 * MemorySection — newest-first feed of what the agent has learnt in this project.
 *
 * Phase 2 of the "Project as ecosystem of work" track (June 8, 2026).
 * Backed by ``GET /projects/{id}/memory/timeline`` which returns the
 * episodic facts owned by this project. **Crucially this view does NOT
 * show the user's general memory** — those facts stay reachable from
 * every chat regardless of project, so surfacing them here would imply
 * they belong to the project (they don't).
 *
 * UX rules:
 *   - Each row shows the fact text, an "appreso" date, and a small chip
 *     with the ``fact_type`` (preference / project / relationship / …).
 *     Confidence and salience are stored but hidden from the user —
 *     they read like internal model knobs more than user-facing
 *     signals.
 *   - The helper banner spells out the asymmetric inheritance rule so
 *     the user never confuses project memory with general memory.
 *     This is the same rule documented in the BE migration's docstring.
 *   - Empty state explains *why* it's empty: this surface fills in
 *     after the agent finishes a turn inside the project (the Temporal
 *     pipeline tags facts at write-time, not retroactively).
 */

import { msg } from "@lingui/core/macro";
import type { I18n } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import { Brain, ExternalLink, Loader2 } from "lucide-react";
import * as React from "react";

import type { ProjectMemoryEntry } from "@ando/core/api";
import { AlertBanner, SurfaceSkeleton, cn, toast } from "@ando/ui";

import { useProjectMemoryTimeline, useUpdateProject } from "../../hooks/useProjects.js";
import { formatRelativeDateI18n } from "../../utils/relativeDate.js";

export interface MemorySectionProps {
  readonly projectId: string;
  /** Full project row — needed for the Phase 2.5 transfer-policy toggle. */
  readonly project: import("@ando/core/api").Project;
}

// Date formatting moved to ``utils/relativeDate`` — single source of
// truth shared with Artifact / Tasks / Automations (code review D1).
// Memory is past-facing: skip "domani" recognition. The Lingui-aware
// variant is resolved inline at the consumer so the anchor labels
// (``oggi`` / ``ieri``) round-trip through the extracted catalog.

/** Localised label for the fact_type chip — falls back to the raw token. */
function formatFactType(raw: string | null, i18n: I18n): string | null {
  if (!raw) return null;
  const map: Readonly<Record<string, string>> = {
    knowledge: i18n._(msg`Conoscenza`),
    preference: i18n._(msg`Preferenza`),
    project: i18n._(msg`Progetto`),
    relationship: i18n._(msg`Relazione`),
    decision: i18n._(msg`Decisione`),
    promise: i18n._(msg`Promessa`),
    date: i18n._(msg`Data`),
    action_feedback: i18n._(msg`Feedback`),
  };
  return map[raw] ?? raw;
}

const TimelineRow: React.FC<{ readonly entry: ProjectMemoryEntry }> = ({
  entry,
}) => {
  const { i18n } = useLingui();
  const factType = formatFactType(entry.fact_type, i18n);
  const date = formatRelativeDateI18n(entry.created_at, i18n, {
    recogniseTomorrow: false,
  });
  const conversationId = entry.source_conversation_id;
  const linkable = conversationId !== null;

  return (
    <li
      className={cn(
        "flex items-start gap-2.5 rounded-md px-2.5 py-2",
        "transition-colors hover:bg-ink-1/[0.025]",
      )}
    >
      <span
        aria-hidden
        className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-ando-orange-soft"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-ando-orange" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-ink-1 leading-snug" title={entry.fact}>
          {entry.fact}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10.5px] text-ink-3 tabular-nums">
          <span>{date}</span>
          {factType ? (
            <>
              <span aria-hidden>·</span>
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-1.5 py-0.5 font-medium",
                  "non-tabular-nums bg-ink-1/[0.05] text-ink-2",
                )}
              >
                {factType}
              </span>
            </>
          ) : null}
          {entry.mention_count && entry.mention_count > 1 ? (
            <>
              <span aria-hidden>·</span>
              <span className="non-tabular-nums">
                <Trans>menzionato {entry.mention_count}×</Trans>
              </span>
            </>
          ) : null}
        </div>
      </div>
      {linkable ? (
        <Link
          to="/workspace/conversations"
          search={{ focus: conversationId } as { focus: string }}
          aria-label={i18n._(msg`Apri la conversazione che ha generato questo ricordo`)}
          title={i18n._(msg`Apri conversazione`)}
          className={cn(
            "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md",
            "text-ink-3 transition-colors hover:bg-ink-1/[0.06] hover:text-ink-1",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
          )}
        >
          <ExternalLink className="h-3 w-3" aria-hidden />
        </Link>
      ) : null}
    </li>
  );
};

/**
 * TransferPolicyToggle — flip the project between ``isolated`` and
 * ``shared`` cross-project transfer policies. Surfaces the GDPR
 * implications inline so the user understands the consequences of the
 * flip BEFORE confirming. The mutation invalidates the projects list so
 * the ProjectPill and Hub re-read the new policy without a manual
 * refresh.
 */
const TransferPolicyToggle: React.FC<{
  readonly project: import("@ando/core/api").Project;
}> = ({ project }) => {
  const { i18n } = useLingui();
  const updateMutation = useUpdateProject();
  const current = project.cross_project_transfer_policy ?? "isolated";
  const isShared = current === "shared";

  const handleFlip = React.useCallback(
    (next: "isolated" | "shared") => {
      if (next === current) return;
      updateMutation.mutate(
        {
          projectId: project.project_id,
          patch: { cross_project_transfer_policy: next },
        },
        {
          onSuccess: () => {
            toast.success(
              next === "shared"
                ? i18n._(msg`Memoria del progetto ora condivisa`)
                : i18n._(msg`Memoria del progetto ora isolata`),
            );
          },
          onError: (err) => {
            toast.error(
              err instanceof Error
                ? i18n._(msg`Impossibile aggiornare la policy: ${err.message}`)
                : i18n._(msg`Impossibile aggiornare la policy`),
            );
          },
        },
      );
    },
    [current, project.project_id, updateMutation, i18n],
  );

  const optionClass = (active: boolean): string =>
    cn(
      "flex flex-1 cursor-pointer flex-col gap-1 rounded-md border px-3 py-2.5 text-left transition-colors",
      "focus-within:ring-2 focus-within:ring-ando-orange/45",
      active
        ? "border-ando-orange/45 bg-ando-orange-soft/40"
        : "border-hairline bg-white/70 hover:bg-ink-1/[0.03]",
      updateMutation.isPending && "opacity-60",
    );

  return (
    <div className="flex flex-col gap-2 rounded-md border border-hairline bg-card/40 px-3 py-3">
      <div className="font-medium text-[12px] text-ink-2">
        <Trans>Condivisione con altri progetti</Trans>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <label className={optionClass(!isShared)}>
          <input
            type="radio"
            name={`policy-${project.project_id}`}
            value="isolated"
            checked={!isShared}
            onChange={() => handleFlip("isolated")}
            disabled={updateMutation.isPending}
            className="sr-only"
          />
          <span className="font-medium text-[12.5px] text-ink-1">
            <Trans>Isolato</Trans>
            <span className="ml-1.5 text-[10.5px] text-ink-3">
              <Trans>predefinito</Trans>
            </span>
          </span>
          <span className="text-[11.5px] text-ink-3 leading-snug">
            <Trans>
              La memoria di questo progetto non viene mai mostrata in altri
              progetti. Consigliato per clienti con dati separati per privacy
              (GDPR).
            </Trans>
          </span>
        </label>
        <label className={optionClass(isShared)}>
          <input
            type="radio"
            name={`policy-${project.project_id}`}
            value="shared"
            checked={isShared}
            onChange={() => handleFlip("shared")}
            disabled={updateMutation.isPending}
            className="sr-only"
          />
          <span className="font-medium text-[12.5px] text-ink-1">
            <Trans>Condiviso</Trans>
          </span>
          <span className="text-[11.5px] text-ink-3 leading-snug">
            <Trans>
              I ricordi entrano nel pool dei progetti "condivisi" dello stesso
              utente. Utile per famiglie di progetti correlati. NON usare per
              dati separati per cliente.
            </Trans>
          </span>
        </label>
      </div>
    </div>
  );
};

export const MemorySection: React.FC<MemorySectionProps> = ({ projectId, project }) => {
  const { i18n } = useLingui();
  const query = useProjectMemoryTimeline(projectId);

  // Helper banner — surfaces the asymmetric inheritance rule so the
  // user understands why the workspace-wide preferences don't show
  // up here (they apply to this project too, but they live elsewhere).
  // Uses the @ando/ui AlertBanner ``info`` tone — consistent palette
  // with every other inline notice in the project track.
  const InheritanceNote: React.FC = () => (
    <AlertBanner tone="info">
      <Trans>
        Qui vedi quello che l'agente ha imparato lavorando in questo
        progetto. Le preferenze generali del tuo workspace (nome, ruolo,
        gusti) restano valide anche qui, ma non vengono elencate per non
        confondere i due livelli. Gli altri progetti non si mescolano: la
        memoria di questo progetto resta isolata.
      </Trans>
    </AlertBanner>
  );

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <InheritanceNote />
        <TransferPolicyToggle project={project} />
        <SurfaceSkeleton
          variant="card-row"
          count={4}
          ariaLabel={i18n._(msg`Caricamento memoria`)}
        />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="flex flex-col gap-3">
        <InheritanceNote />
        <TransferPolicyToggle project={project} />
        <AlertBanner tone="error">
          <Trans>
            Impossibile caricare la memoria del progetto. Riprova fra poco.
          </Trans>
        </AlertBanner>
      </div>
    );
  }

  const entries = query.data ?? [];

  return (
    <div className="flex flex-col gap-3">
      <InheritanceNote />
      <TransferPolicyToggle project={project} />
      {entries.length === 0 ? (
        <div className="flex flex-col items-start gap-2 rounded-md border border-hairline border-dashed bg-card/40 px-4 py-6 text-[12.5px] text-ink-2 leading-relaxed">
          <Brain className="h-4 w-4 text-ink-3" aria-hidden />
          <span>
            <Trans>
              L'agente non ha ancora memorizzato nulla in questo progetto.
              Inizia una conversazione: al termine, i fatti rilevanti
              vengono riassunti qui automaticamente.
            </Trans>
          </span>
        </div>
      ) : (
        <>
          <div className="text-[11.5px] text-ink-3">
            <Trans>
              <strong className="font-medium text-ink-2">{entries.length}</strong>{" "}
              ricordi del progetto, più recenti per primi.
            </Trans>
          </div>
          <ul className="flex flex-col gap-1">
            {entries.map((e) => (
              <TimelineRow key={e.fact_id} entry={e} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

MemorySection.displayName = "MemorySection";
